import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Plug, Shield, ExternalLink, CheckCircle2, Clock, XCircle, KeyRound, RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  PageShell, PageHeader, Modal, PrimaryButton, GhostButton, Pill,
  Loader, Alert, NAVY, CHAMPAGNE, GOLD, IVORY, SLATE, GREEN_LEAF, RED_LEAF,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { altioraToast } from "@/components/altiora/toast";

const STATUS_META = {
  connected:                  { label: "Connecté",       tone: "green", icon: CheckCircle2 },
  configured_pending_test:    { label: "En attente",     tone: "amber", icon: Clock },
  never_configured:           { label: "Non configuré",  tone: "neutral", icon: XCircle },
};

export default function IntegrationsHub() {
  const [items, setItems] = useState(null);
  const [target, setTarget] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [env, setEnv] = useState("sandbox");

  const load = () => api.get("/integrations/status").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const connect = async () => {
    try {
      await api.post("/integrations/connect", {
        provider: target.provider, api_key: apiKey, environment: env,
      });
      altioraToast.success("Connexion enregistrée");
      setTarget(null); setApiKey("");
      await load();
    } catch (e) {
      altioraToast.error(e?.response?.data?.detail || "Impossible d'enregistrer.");
    }
  };

  const sync = async (provider) => {
    try {
      const r = await api.post(`/integrations/sync/${provider}`);
      altioraToast.info(r.data?.message || "Sync terminée");
      await load();
    } catch (e) {
      altioraToast.error(e?.response?.data?.detail || "Sync impossible.");
    }
  };

  return (
    <PageShell testid="integrations-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-12 pb-20">
        <PageHeader
          eyebrow="ALTIORA · Intégrations partenaires"
          title="Vos fournisseurs connectés"
          subtitle="Reliez ALTIORA à vos fournisseurs pour synchroniser polices, commissions et renouvellements. Les intégrations sont livrées en mode STUB — la clé API est enregistrée en toute sécurité, la vraie synchronisation s'active dès que le connecteur passe en production."
          testid="integrations-header"
        />
        {!items && <Loader label="Chargement…" />}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="integrations-grid">
          {items?.map((it) => {
            const meta = STATUS_META[it.status] || STATUS_META.never_configured;
            const Icon = meta.icon;
            return (
              <motion.div key={it.provider} whileHover={{ y: -2 }}
                          className="rounded-xl bg-white p-5"
                          style={{ border: `1px solid ${CHAMPAGNE}33` }}
                          data-testid={`integration-card-${it.provider}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.28em]" style={{ color: CHAMPAGNE }}>
                      {it.kind} · {it.regions.join(" · ")}
                    </div>
                    <h3 className="mt-1 font-serif text-xl tracking-tight" style={{ color: NAVY }}>
                      {it.label}
                    </h3>
                  </div>
                  <Pill tone={meta.tone} testid={`integration-status-${it.provider}`}>
                    <Icon className="h-3 w-3 mr-1 inline" /> {meta.label}
                  </Pill>
                </div>
                <div className="mt-3 text-[11px]" style={{ color: SLATE }}>
                  {it.environment && `Environnement : ${it.environment}`}
                  {it.last_sync_at && ` · Dernière sync : ${it.last_sync_at.slice(0, 10)}`}
                </div>
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <PrimaryButton onClick={() => setTarget(it)} testid={`integration-connect-${it.provider}`}>
                    <KeyRound className="h-3 w-3 mr-1 inline" />
                    {it.status === "never_configured" ? "Connecter" : "Reconfigurer"}
                  </PrimaryButton>
                  {it.status !== "never_configured" && (
                    <GhostButton onClick={() => sync(it.provider)} testid={`integration-sync-${it.provider}`}>
                      <RefreshCw className="h-3 w-3 mr-1 inline" /> Synchroniser
                    </GhostButton>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        <Modal open={!!target} onClose={() => setTarget(null)}
               title={target ? `Connecter ${target.label}` : ""}
               description="Fournissez votre clé API — elle sera stockée hachée. En mode STUB, aucun appel réel n'est effectué."
               testid="integration-modal">
          <div className="space-y-4">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: SLATE }}>
                Clé API
              </label>
              <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                     className="mt-1 w-full rounded-md px-3 py-2 text-sm"
                     style={{ border: `1px solid ${CHAMPAGNE}55`, color: NAVY }}
                     data-testid="integration-api-key-input" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: SLATE }}>
                Environnement
              </label>
              <select value={env} onChange={(e) => setEnv(e.target.value)}
                      className="mt-1 w-full rounded-md px-3 py-2 text-sm bg-white"
                      style={{ border: `1px solid ${CHAMPAGNE}55`, color: NAVY }}
                      data-testid="integration-env-select">
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
            </div>
            <Alert variant="info" title="Mode STUB actif">
              La clé sera enregistrée en toute sécurité mais l'appel au fournisseur reste simulé
              tant que l'équipe ALTIORA n'a pas branché la vraie API du partenaire.
            </Alert>
            <div className="flex justify-end gap-2">
              <GhostButton onClick={() => setTarget(null)}>Annuler</GhostButton>
              <PrimaryButton onClick={connect} disabled={!apiKey} testid="integration-modal-save">
                Enregistrer la connexion
              </PrimaryButton>
            </div>
          </div>
        </Modal>
      </div>
    </PageShell>
  );
}
